/** Shared editor/CLI/runtime projection for custom-screen child data sources.
 * Pure: payment localization is injected by the published runtime only.
 */
import type { ChildSectionData, ChildSectionInit, LocalizedPriceFields } from '@modules/screen-view-v2/types/customScreenBridge.types';

/**
 * Resolve localized text from a V2 label/l10n object.
 * Handles both `{ translations: { en: "..." } }` and plain string fallback.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function resolveLocalizedText(label: any, locale: string): string | undefined {
  if (!label) return undefined;
  if (typeof label === 'string') return label;
  const translations = label.translations;
  if (!translations || typeof translations !== 'object') return undefined;
  return translations[locale] ?? translations['en'] ?? (Object.values(translations)[0] as string);
}

/**
 * Serialize child sections from a CustomEmbed's embedContent.childSections
 * into ChildSectionInit[] for iframe transport.
 *
 * Handles 6 section kinds: ProductCatalog, OptionsList, SingleSelectionList,
 * MultipleSelectionList, BulletList, Text, Media.
 */
export function serializeCustomScreenChildSections(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  childSections: Array<any>,
  locale: string,
  // Optional (additive): when provided, selection items get a resolved `optionId`
  // (variableValue → option id via the bound variable's screenBindings). Omitting
  // them preserves the previous output exactly (optionId stays undefined).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  variables?: Record<string, any>,
  screenId?: string,
  resolvePrice?: (productId: string) => LocalizedPriceFields | undefined,
): ChildSectionInit[] {
  const result: ChildSectionInit[] = [];

  for (const child of childSections) {
    if (!child?.id || !child?.kind) continue;

    const data: ChildSectionData = {};

    switch (child.kind) {
      case 'ProductCatalog': {
        const items = child.productCatalogContent?.items;
        if (Array.isArray(items)) {
          let defaultSelectedIdx: number | null = null;
          data.products = items.map((item: Record<string, unknown>, index: number) => {
            if (item.isSelected) defaultSelectedIdx = index;
            return {
              productId: (item.productId as string) || '',
              index,
              isSelected: Boolean(item.isSelected),
              labels: {
                topStart: resolveLocalizedText(item.topStartLabel, locale),
                bottomStart: resolveLocalizedText(item.bottomStartLabel, locale),
                topEnd: resolveLocalizedText(item.topEndLabel, locale),
                bottomEnd: resolveLocalizedText(item.bottomEndLabel, locale),
                description: resolveLocalizedText(item.descriptionLabel, locale),
              },
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              badge: (item.badge as any)?.text
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                ? { text: resolveLocalizedText((item.badge as any).text, locale) || '' }
                : null,
            };
          });
          data.selectedProductIndex = defaultSelectedIdx !== null ? defaultSelectedIdx : (items.length > 0 ? 0 : null);

          // Resolve localized prices and substitute @price tags in labels
          if (resolvePrice) {
            const prices: Record<string, LocalizedPriceFields> = {};
            for (const product of data.products) {
              const priceFields = resolvePrice(product.productId);
              if (!priceFields) continue;
              prices[product.productId] = priceFields;

              // Substitute @price, @interval etc. in product labels
              const vars: Record<string, string> = {
                price: priceFields.displayPrice,
                priceAmount: priceFields.priceAmount,
                priceAndCurrency: priceFields.priceAndCurrency,
                priceandcurrency: priceFields.priceAndCurrency,
                duration: priceFields.duration,
                interval: priceFields.interval,
                trialDays: priceFields.trialDays,
                fullTrialDays: priceFields.fullTrialDays,
                introPrice: priceFields.introPrice,
                introDuration: priceFields.introDuration,
              };
              const labels = product.labels as Record<string, string | undefined>;
              for (const key of Object.keys(labels)) {
                if (labels[key]) {
                  labels[key] = labels[key]!.replace(/@(\w+)/g, (match, name) => vars[name] ?? match);
                }
              }
            }
            if (Object.keys(prices).length > 0) {
              data.productPrices = prices;
            }
          }
        }
        break;
      }

      case 'OptionsList':
      case 'SingleSelectionList':
      case 'MultipleSelectionList':
      case 'BulletList': {
        const items = child.optionsListContent?.items;
        if (Array.isArray(items)) {
          const optionByValue = resolveOptionIdByValue(child.id, screenId, variables);
          data.options = items.map((item: Record<string, unknown>, index: number) => {
            const variableValue = (item.variableValue as string) || undefined;
            return {
              index,
              title: resolveLocalizedText(item.title, locale),
              subtitle: resolveLocalizedText(item.subtitle, locale),
              imageUrl: resolveLocalizedText(item.image, locale) || undefined,
              variableValue,
              // Additive: resolved bound-variable option id for this item (stable by value, not position)
              optionId: variableValue ? optionByValue[variableValue] : undefined,
              isSelected: false,
            };
          });
        }
        break;
      }

      case 'Text': {
        const tc = child.textContent;
        if (tc) {
          data.title = resolveLocalizedText(tc.title, locale);
          data.subtitle = resolveLocalizedText(tc.subtitle, locale);
          data.subtitle2 = resolveLocalizedText(tc.subtitle2, locale);
        }
        break;
      }

      case 'Button': {
        const text = resolveLocalizedText(child.buttonContent?.content, locale);
        data.buttonText = text;
        data.title = text;
        break;
      }

      case 'Media': {
        const mc = child.mediaContent;
        if (mc) {
          data.mediaType = (mc.kind as string) || 'Image';
          // FeaturedMedia stores localized assets in content.image / content.video
          const content = mc.content as any;
          const localizedAsset = content?.image ?? content?.video;
          if (localizedAsset) {
            const translations = localizedAsset.translations;
            if (translations && typeof translations === 'object') {
              const resolved = translations[locale] ?? translations['en'] ?? Object.values(translations)[0];
              data.mediaUrl = typeof resolved === 'string' ? resolved : resolved?.original ?? resolved?.small;
            }
          }
        }
        break;
      }
    }

    result.push({ id: child.id, kind: child.kind, label: child.label || '', data });
  }

  return result;
}

/**
 * Build a { variableValue → optionId } map for a selection section, using the
 * bound enum/enum[] variable's screenBindings (the same reverse lookup the native
 * picker uses). Returns {} when there is no bound variable / no bindings — callers
 * then leave `optionId` undefined (previous behavior).
 *
 * The variable is matched to the section by `boundSectionId === sectionId` (precise),
 * falling back to a screen-bound variable with no section scope.
 */
export function customScreenBoundVariableCandidates<T extends { boundSectionId?: string; boundScreenIds?: string[] }>(
  sectionId: string, screenId: string | undefined, variables: Record<string, T> | undefined,
): T[] {
  if (!variables || !screenId) return [];
  const rows = Object.values(variables).filter(v => v && typeof v === 'object');
  const precise = rows.filter(v => v.boundSectionId === sectionId);
  return precise.length ? precise : rows.filter(v => !v.boundSectionId && Array.isArray(v.boundScreenIds) && v.boundScreenIds.includes(screenId));
}

export function resolveCustomScreenBoundVariable<T extends { boundSectionId?: string; boundScreenIds?: string[] }>(
  sectionId: string, screenId: string | undefined, variables: Record<string, T> | undefined,
): T | undefined {
  const candidates = customScreenBoundVariableCandidates(sectionId, screenId, variables);
  // Ambiguity cannot be resolved by insertion order. The authoring preflight must
  // reject it; preview/runtime leave optionId unavailable instead of inventing it.
  return candidates.length === 1 ? candidates[0] : undefined;
}

function resolveOptionIdByValue(
  sectionId: string, screenId: string | undefined,
  variables: Record<string, any> | undefined,
): Record<string, string> {
  const bound = resolveCustomScreenBoundVariable(sectionId, screenId, variables);
  const bindings = screenId ? bound?.screenBindings?.[screenId] : undefined;
  const map: Record<string, string> = Object.create(null);
  if (!bindings || typeof bindings !== 'object' || Array.isArray(bindings)) return map;
  for (const [optionId, variableValue] of Object.entries(bindings)) {
    if (typeof variableValue !== 'string') continue;
    if (Object.prototype.hasOwnProperty.call(map, variableValue)) return Object.create(null);
    map[variableValue] = optionId;
  }
  return map;
}

/** Self-contained: also injected into the isolated iframe SDK. Exact ids win;
 * duplicate ids/labels never select a different source based on array order. */
export function findCustomScreenChildSection<T extends { id?: string; label?: string; kind?: string }>(
  sections: T[] | null | undefined, nameOrId: string, allowProductSingleton = false,
): T | null {
  if (!sections || !nameOrId) return null;
  const exact = sections.filter(section => section && section.id === nameOrId);
  if (exact.length) return exact.length === 1 ? exact[0] : null;
  const labelled = sections.filter(section => section && section.label === nameOrId);
  if (labelled.length) return labelled.length === 1 ? labelled[0] : null;
  if (allowProductSingleton && nameOrId === 'ProductCatalog') {
    const products = sections.filter(section => section && section.kind === 'ProductCatalog');
    return products.length === 1 ? products[0] : null;
  }
  return null;
}
